import { useState, useEffect, useRef } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  sendEmailVerification,
  OAuthProvider,
  linkWithCredential,
  fetchSignInMethodsForEmail,
  getAdditionalUserInfo,
  signInWithCustomToken,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase.js";
import { X, Loader2, UploadCloud, FileText } from "lucide-react";
import { DIAL_CODES, DEFAULT_DIAL_CODE, COUNTRIES } from "@/data/countries";
import {
  isValidMobileNumber,
  isValidPassportName,
  isValidPassportNumber,
  isPastDate,
  isExpiryWithinTenYears,
  GENDER_OPTIONS,
} from "@/utils/validators";
import {
  uploadKtpDocument,
  KTP_MAX_TOTAL_SIZE,
} from "../services/profileService";
import { useAuth } from "../Authcontext.jsx";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useSiteTexts } from "@/cms/useSiteText";

const INDONESIA = "Indonesia";

const GoogleIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
      fill="#4285F4"
    />
    <path
      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
      fill="#34A853"
    />
    <path
      d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"
      fill="#FBBC05"
    />
    <path
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z"
      fill="#EA4335"
    />
  </svg>
);

const InputField = ({
  label,
  type = "text",
  placeholder,
  value,
  onChange,
  error,
}) => (
  <div className="mb-3">
    <label className="block text-xs font-medium text-gray-500 mb-1">
      {label}
    </label>
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className="w-full h-11 rounded-xl border border-gray-200 bg-gray-50 px-3.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-[#0B2421]/30 focus:border-[#0B2421]"
    />
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const SelectField = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  error,
}) => (
  <div className="mb-3">
    <label className="block text-xs font-medium text-gray-500 mb-1">
      {label}
    </label>
    <select
      value={value}
      onChange={onChange}
      className="w-full h-11 rounded-xl border border-gray-200 bg-gray-50 px-3.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-[#0B2421]/30 focus:border-[#0B2421]"
    >
      <option value="">{placeholder}</option>
      {options.map((opt) =>
        typeof opt === "string" ? (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ) : (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ),
      )}
    </select>
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const ErrorBanner = ({ msg }) =>
  msg ? (
    <div className="mb-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
      {msg}
    </div>
  ) : null;

function friendlyError(code) {
  const map = {
    "auth/email-already-in-use":
      "This email is already registered. Sign in instead.",
    "auth/invalid-email": "Enter a valid email address.",
    "auth/weak-password": "Password must be at least 6 characters.",
    "auth/user-not-found": "No account with this email.",
    "auth/wrong-password": "Incorrect password.",
    "auth/invalid-credential": "Incorrect email or password.",
    "auth/too-many-requests": "Too many attempts. Try again later.",
    "auth/popup-closed-by-user": "Google sign-in was cancelled.",
    "auth/network-request-failed": "Network error. Check your connection.",
  };
  return map[code] ?? "Something went wrong. Please try again.";
}

/**
 * Modal login/signup gate. Reuses the same Firebase auth flow as the
 * full-page Login screen, but scoped to a dialog so it can interrupt an
 * in-progress action (like a payment confirm) without navigating away.
 *
 * TWO THINGS FIXED HERE:
 *
 * 1. `handleSignUp` writes the Firestore `users/{uid}` doc IMMEDIATELY
 *    after `createUserWithEmailAndPassword` succeeds, not after email
 *    verification. A freshly-created account never exists without a
 *    matching profile doc, even for a split second.
 *
 * 2. The app's AuthContext treats "logged in" as "signed in AND
 *    emailVerified" — that rule is intentional and unchanged. So this
 *    modal now enforces verification itself before it EVER calls
 *    `onAuthenticated` or lets itself close: signup -> profile step ->
 *    verify step (blocks on emailVerified via reload) -> only then
 *    onAuthenticated. That way the moment onAuthenticated fires, the app
 *    context and this modal always agree the user is really logged in —
 *    no split-brain, no surprise redirect later on a protected page.
 *
 * The modal can't be dismissed once a new signup is mid-flow (profile or
 * verify step) — no X button, no backdrop-click-to-close — so a user
 * can't accidentally strand a half-finished, unverified account.
 *
 * @param {{
 *   open: boolean;
 *   onClose: () => void;
 *   onAuthenticated: (user: import("firebase/auth").User) => void; // called once signed in (and, for new users, profile completed)
 * }} props
 */
export default function AuthGateModal({ open, onClose, onAuthenticated }) {
  const { refreshUser } = useAuth();
  const [view, setView] = useState("login"); // "login" | "signup" | "profile" | "verify"
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pendingUser, setPendingUser] = useState(null);
  const [pendingProvider, setPendingProvider] = useState("email");
  const [checkingVerified, setCheckingVerified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(false);

  // ---- Profile-completion step state (new signups only) ----
  const [mobileCountryCode, setMobileCountryCode] = useState(DEFAULT_DIAL_CODE);
  const [mobileNumber, setMobileNumber] = useState("");
  const [passportNumber, setPassportNumber] = useState("");
  const [fullNameOnPassport, setFullNameOnPassport] = useState("");
  const [nationality, setNationality] = useState("");
  const [birthCountry, setBirthCountry] = useState("");
  const [gender, setGender] = useState("");
  const [isBatamLocal, setIsBatamLocal] = useState("");
  const [ktpFrontUrl, setKtpFrontUrl] = useState("");
  const [ktpBackUrl, setKtpBackUrl] = useState("");
  // Byte sizes of whatever's currently uploaded this session, used to
  // enforce the combined 5MB budget across both files — reset per side
  // on replace.
  const [ktpFrontSize, setKtpFrontSize] = useState(0);
  const [ktpBackSize, setKtpBackSize] = useState(0);
  const [isUploadingKtpFront, setIsUploadingKtpFront] = useState(false);
  const [isUploadingKtpBack, setIsUploadingKtpBack] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [dateOfIssue, setDateOfIssue] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [profileErrors, setProfileErrors] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);

  const popupRef = useRef(null);
  const pollRef = useRef(null);
  const checkSessionRef = useRef(null);
  const handledRef = useRef(false); // guards against double-handling
  const finishSingpassLoginRef = useRef(null); // set by handleSingpassSignIn, called by BroadcastChannel listener

  const clearError = () => setError("");

  const getErrorCode = (err) =>
    typeof err === "object" && err !== null && "code" in err
      ? (err.code ?? "")
      : "";

  // Called immediately on account creation — this is the fix. Never gate
  // this behind email verification.
  const createFirestoreUser = async (user, name, provider) => {
    await setDoc(doc(db, "users", user.uid), {
      uid: user.uid,
      email: user.email,
      fullName: name,
      photoURL: user.photoURL ?? "",
      provider,
      role: "user",
      emailVerified: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  };

  const upsertUserDoc = async (user, provider) => {
    await setDoc(
      doc(db, "users", user.uid),
      {
        uid: user.uid,
        email: user.email,
        fullName: user.displayName ?? "",
        photoURL: user.photoURL ?? "",
        provider,
        role: "user",
        emailVerified: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  };

  const matchCountryName = (desc) => {
    if (!desc) return "";
    const needle = desc.toUpperCase();
    const found = COUNTRIES.find(
      (c) =>
        needle.includes(c.name.toUpperCase()) ||
        c.name.toUpperCase().includes(needle),
    );
    return found?.name || "";
  };

  const matchGender = (desc) => {
    if (!desc) return "";
    const needle = desc.toUpperCase();
    const found = GENDER_OPTIONS.find((g) => {
      const label = typeof g === "string" ? g : g.label;
      return (
        label.toUpperCase() === needle || needle.includes(label.toUpperCase())
      );
    });
    return typeof found === "string" ? found : found?.value || "";
  };

  const prefillFromSingpassProfile = (profile) => {
    resetProfileFields();
    if (!profile) return;

    setFullName(profile.fullName || "");
    setFullNameOnPassport((profile.fullName || "").toUpperCase());

    if (profile.mobile_number) {
      const [code, ...rest] = profile.mobile_number.trim().split(" ");
      if (code) setMobileCountryCode(code);
      if (rest.length) setMobileNumber(rest.join("").replace(/[^0-9]/g, ""));
    }

    const pd = profile.passport_details || {};
    setPassportNumber(pd.passport_number || "");
    setDateOfBirth(pd.date_of_birth || "");
    setExpiryDate(pd.passport_expiry_date || "");
    setNationality(matchCountryName(pd.nationality));
    setBirthCountry(matchCountryName(profile.birthCountry));
    setGender(matchGender(profile.gender));
  };

  const handleSingpassSignIn = async () => {
    clearError();
    setLoading(true);
    try {
      const functions = getFunctions();
      const singpassInitiate = httpsCallable(functions, "singpassInitiate");
      const { data } = await singpassInitiate({ flow: "popup" });

      const popup = window.open(
        data.authorizeUrl,
        "singpass-login",
        "width=480,height=720",
      );
      if (!popup) {
        setError(
          "Please allow pop-ups for this site to sign in with Singpass.",
        );
        setLoading(false);
        return;
      }
      popupRef.current = popup;
      handledRef.current = false; // new ref, guards against double-handling

      const finishSingpassLogin = async (customToken, profile, error) => {
        if (handledRef.current) return;
        handledRef.current = true;
        clearInterval(pollRef.current);
        clearInterval(checkSessionRef.current);
        popupRef.current?.close();

        if (error) {
          setError("Singpass login failed. Please try again.");
          setLoading(false);
          return;
        }
        try {
          const result = await signInWithCustomToken(auth, customToken);
          setPendingUser(result.user);
          setPendingProvider("singpass");
          prefillFromSingpassProfile(profile);
          setView("profile");
        } catch {
          setError("Singpass sign-in failed. Please try again.");
        } finally {
          setLoading(false);
        }
      };
      finishSingpassLoginRef.current = finishSingpassLogin;

      // Fallback: poll the Cloud Function every 1.5s in case BroadcastChannel
      // never reaches us (e.g. popup callback is on a different origin,
      // such as a tunnel URL during local dev against Singpass staging).
      const singpassCheckSession = httpsCallable(
        functions,
        "singpassCheckSession",
      );
      checkSessionRef.current = setInterval(async () => {
        try {
          const { data: check } = await singpassCheckSession({
            state: data.state,
          });
          if (check.status === "done") {
            finishSingpassLogin(check.customToken, check.profile);
          }
        } catch {
          // ignore transient errors, keep polling until popup closes
        }
      }, 1500);

      pollRef.current = setInterval(() => {
        if (popupRef.current?.closed) {
          clearInterval(pollRef.current);
          clearInterval(checkSessionRef.current);
          if (!handledRef.current) setLoading(false);
        }
      }, 500);
    } catch (err) {
      setError("Could not start Singpass login. Try again.");
      setLoading(false);
    }
  };

  // Listen for the popup's callback page posting back the result.
  // REPLACE the entire useEffect that does window.addEventListener("message", handleMessage)
  // with this:
  useEffect(() => {
    const channel = new BroadcastChannel("singpass-auth");

    const handleMessage = async (event) => {
      if (event.data?.type !== "singpass-auth-complete") return;

      clearInterval(pollRef.current);
      popupRef.current?.close();

      if (event.data.error) {
        setError("Singpass login failed. Please try again.");
        setLoading(false);
        return;
      }

      const { customToken, profile } = event.data;
      try {
        const result = await signInWithCustomToken(auth, customToken);
        setPendingUser(result.user);
        setPendingProvider("singpass");
        prefillFromSingpassProfile(profile);
        setView("profile");
      } catch (err) {
        setError("Singpass sign-in failed. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    channel.onmessage = handleMessage;
    return () => {
      channel.close();
      clearInterval(pollRef.current);
    };
  }, []);

  const getProviderForId = (providerId) => {
    if (providerId === GoogleAuthProvider.PROVIDER_ID)
      return new GoogleAuthProvider();
    if (providerId === FacebookAuthProvider.PROVIDER_ID)
      return new FacebookAuthProvider();
    return new OAuthProvider(providerId);
  };

  const resetProfileFields = () => {
    setMobileCountryCode(DEFAULT_DIAL_CODE);
    setMobileNumber("");
    setPassportNumber("");
    setFullNameOnPassport("");
    setNationality("");
    setBirthCountry("");
    setGender("");
    setIsBatamLocal("");
    setKtpFrontUrl("");
    setKtpBackUrl("");
    setKtpFrontSize(0);
    setKtpBackSize(0);
    setDateOfBirth("");
    setDateOfIssue("");
    setExpiryDate("");
    setProfileErrors({});
  };

  const handleSignIn = async () => {
    clearError();
    if (!loginEmail || !loginPassword)
      return setError("Enter your email and password.");
    setLoading(true);
    try {
      const result = await signInWithEmailAndPassword(
        auth,
        loginEmail,
        loginPassword,
      );
      if (!result.user.emailVerified) {
        setPendingUser(result.user);
        setPendingProvider("email");
        sendEmailVerification(result.user).catch(() => {});
        setView("verify");
        return;
      }
      // Existing, verified user, doc already exists from their original
      // signup — go straight through, no profile step, no navigation.
      onAuthenticated(result.user);
    } catch (err) {
      setError(friendlyError(getErrorCode(err)));
    } finally {
      setLoading(false);
    }
  };

  const handleSocialSignIn = async (provider, providerName) => {
    clearError();
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, provider);
      const isNewUser = getAdditionalUserInfo(result)?.isNewUser;
      await upsertUserDoc(result.user, providerName);
      if (isNewUser) {
        setPendingUser(result.user);
        setPendingProvider(providerName);
        setFullName(result.user.displayName || "");
        resetProfileFields();
        setView("profile");
      } else if (!result.user.emailVerified) {
        // Rare for social sign-in, but handle it the same way as email —
        // never call onAuthenticated for an unverified user.
        setPendingUser(result.user);
        setPendingProvider(providerName);
        sendEmailVerification(result.user).catch(() => {});
        setView("verify");
      } else {
        onAuthenticated(result.user);
      }
    } catch (err) {
      const code = getErrorCode(err);
      if (code === "auth/account-exists-with-different-credential") {
        try {
          const pendingCred =
            providerName === "facebook"
              ? FacebookAuthProvider.credentialFromError(err)
              : GoogleAuthProvider.credentialFromError(err);
          const email = err.customData?.email;
          const methods = await fetchSignInMethodsForEmail(auth, email);
          if (methods.length === 0) {
            setError(
              "This email is already in use. Try a different sign-in method.",
            );
            return;
          }
          const existingProviderId = methods[0];
          const existingProvider = getProviderForId(existingProviderId);
          const existingResult = await signInWithPopup(auth, existingProvider);
          if (pendingCred)
            await linkWithCredential(existingResult.user, pendingCred);
          await upsertUserDoc(
            existingResult.user,
            existingProviderId.replace(".com", ""),
          );
          onAuthenticated(existingResult.user);
        } catch (linkErr) {
          setError(friendlyError(getErrorCode(linkErr)));
        }
      } else {
        setError(friendlyError(code));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async () => {
    clearError();
    if (!fullName.trim()) return setError("Enter your full name.");
    if (!signupEmail) return setError("Enter your email.");
    if (signupPassword.length < 6)
      return setError("Password must be at least 6 characters.");
    if (signupPassword !== confirmPassword)
      return setError("Passwords don't match.");
    setLoading(true);
    try {
      const result = await createUserWithEmailAndPassword(
        auth,
        signupEmail,
        signupPassword,
      );
      // Firestore doc created right here, synchronously in this flow —
      // THIS is the fix for the redirect. The account never exists
      // without a matching profile doc, even for a split second past
      // this function returning.
      await createFirestoreUser(result.user, fullName, "email");
      // Send verification now — we'll actually wait on this one later
      // (in the verify step), so make sure it went out.
      await sendEmailVerification(result.user).catch(() => {});
      setPendingUser(result.user);
      setPendingProvider("email");
      resetProfileFields();
      setView("profile");
    } catch (err) {
      setError(friendlyError(getErrorCode(err)));
    } finally {
      setLoading(false);
    }
  };

  const handleKtpUpload = async (side, file) => {
    if (!file || !pendingUser) return;
    const setUploading =
      side === "front" ? setIsUploadingKtpFront : setIsUploadingKtpBack;
    const setUrl = side === "front" ? setKtpFrontUrl : setKtpBackUrl;
    const setSize = side === "front" ? setKtpFrontSize : setKtpBackSize;
    const existingUrl = side === "front" ? ktpFrontUrl : ktpBackUrl;
    const otherSideSize = side === "front" ? ktpBackSize : ktpFrontSize;

    if (otherSideSize + file.size > KTP_MAX_TOTAL_SIZE) {
      setProfileErrors((e) => ({
        ...e,
        [side === "front" ? "ktpFrontUrl" : "ktpBackUrl"]:
          "KTP front and back must together be under 5MB. Try a smaller or more compressed file.",
      }));
      return;
    }

    setUploading(true);
    try {
      const url = await uploadKtpDocument(
        pendingUser.uid,
        file,
        side,
        existingUrl,
      );
      setUrl(url);
      setSize(file.size);
    } catch (err) {
      setProfileErrors((e) => ({
        ...e,
        [side === "front" ? "ktpFrontUrl" : "ktpBackUrl"]:
          err.message || "Failed to upload image. Please try again.",
      }));
    } finally {
      setUploading(false);
    }
  };

  const handleNationalityChange = (value) => {
    setNationality(value);
    if (value !== INDONESIA) {
      setIsBatamLocal("");
      setKtpFrontUrl("");
      setKtpBackUrl("");
      setKtpFrontSize(0);
      setKtpBackSize(0);
    }
  };

  // Every editable string on the profile step — see textRegistry.js for
  // the "auth.*" group. One bulk read instead of ~40 individual
  // useSiteText() calls.
  const t = useSiteTexts({
    profileEyebrow: "auth.profile.eyebrow",
    profileTitle: "auth.profile.title",
    profileSubtext: "auth.profile.subtext",
    profileSubtextEmailNote: "auth.profile.subtextEmailNote",
    sectionContact: "auth.profile.sectionContact",
    sectionPassport: "auth.profile.sectionPassport",
    fieldFullName: "auth.profile.fieldFullName",
    fieldFullNamePlaceholder: "auth.profile.fieldFullNamePlaceholder",
    fieldPassportNumber: "auth.profile.fieldPassportNumber",
    fieldPassportNumberPlaceholder:
      "auth.profile.fieldPassportNumberPlaceholder",
    fieldNationality: "auth.profile.fieldNationality",
    fieldBirthCountry: "auth.profile.fieldBirthCountry",
    fieldBatamLocal: "auth.profile.fieldBatamLocal",
    ktpUploadInstruction: "auth.profile.ktpUploadInstruction",
    fieldKtpFront: "auth.profile.fieldKtpFront",
    fieldKtpBack: "auth.profile.fieldKtpBack",
    ktpFileConstraints: "auth.profile.ktpFileConstraints",
    fieldGender: "auth.profile.fieldGender",
    fieldDateOfBirth: "auth.profile.fieldDateOfBirth",
    fieldDateOfIssue: "auth.profile.fieldDateOfIssue",
    fieldExpiryDate: "auth.profile.fieldExpiryDate",
    savingLabel: "auth.profile.savingLabel",
    saveButton: "auth.profile.saveButton",
    errMobileRequired: "auth.error.mobileRequired",
    errMobileInvalid: "auth.error.mobileInvalid",
    errFullNameRequired: "auth.error.fullNameRequired",
    errFullNameInvalid: "auth.error.fullNameInvalid",
    errPassportNumberRequired: "auth.error.passportNumberRequired",
    errPassportNumberInvalid: "auth.error.passportNumberInvalid",
    errNationalityRequired: "auth.error.nationalityRequired",
    errBirthCountryRequired: "auth.error.birthCountryRequired",
    errBatamLocalRequired: "auth.error.batamLocalRequired",
    errKtpFrontRequired: "auth.error.ktpFrontRequired",
    errKtpBackRequired: "auth.error.ktpBackRequired",
    errGenderRequired: "auth.error.genderRequired",
    errDateOfBirthRequired: "auth.error.dateOfBirthRequired",
    errDateOfBirthPast: "auth.error.dateOfBirthPast",
    errDateOfIssueRequired: "auth.error.dateOfIssueRequired",
    errExpiryRequired: "auth.error.expiryRequired",
    errExpiryTooFar: "auth.error.expiryTooFar",
  });

  const validateProfile = () => {
    const next = {};

    if (!mobileNumber.trim()) {
      next.mobileNumber = t.errMobileRequired;
    } else if (!isValidMobileNumber(mobileNumber)) {
      next.mobileNumber = t.errMobileInvalid;
    }

    if (!fullNameOnPassport.trim()) {
      next.fullNameOnPassport = t.errFullNameRequired;
    } else if (!isValidPassportName(fullNameOnPassport)) {
      next.fullNameOnPassport = t.errFullNameInvalid;
    }

    if (!passportNumber.trim()) {
      next.passportNumber = t.errPassportNumberRequired;
    } else if (!isValidPassportNumber(passportNumber)) {
      next.passportNumber = t.errPassportNumberInvalid;
    }

    if (!nationality) next.nationality = t.errNationalityRequired;
    if (!birthCountry) next.birthCountry = t.errBirthCountryRequired;

    if (nationality === INDONESIA) {
      if (!isBatamLocal) {
        next.isBatamLocal = t.errBatamLocalRequired;
      } else if (isBatamLocal === "Yes") {
        if (!ktpFrontUrl) next.ktpFrontUrl = t.errKtpFrontRequired;
        if (!ktpBackUrl) next.ktpBackUrl = t.errKtpBackRequired;
      }
    }

    if (!gender) next.gender = t.errGenderRequired;

    if (!dateOfBirth) {
      next.dateOfBirth = t.errDateOfBirthRequired;
    } else if (!isPastDate(new Date(dateOfBirth))) {
      next.dateOfBirth = t.errDateOfBirthPast;
    }

    if (!dateOfIssue) next.dateOfIssue = t.errDateOfIssueRequired;

    if (!expiryDate) {
      next.expiryDate = t.errExpiryRequired;
    } else if (
      dateOfIssue &&
      !isExpiryWithinTenYears(new Date(dateOfIssue), new Date(expiryDate))
    ) {
      next.expiryDate = t.errExpiryTooFar;
    }

    setProfileErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleFinishProfile = async () => {
    if (!pendingUser) return;
    if (!validateProfile()) return;

    setSavingProfile(true);
    try {
      await setDoc(
        doc(db, "users", pendingUser.uid),
        {
          fullName,
          mobile_country_code: mobileCountryCode,
          mobile_number: mobileNumber,
          passport_details: {
            passport_number: passportNumber,
            full_name_on_passport: fullNameOnPassport,
            nationality,
            birth_country: birthCountry,
            gender,
            is_batam_local:
              nationality === INDONESIA ? isBatamLocal === "Yes" : "No",
            ktp_front_url:
              nationality === INDONESIA && isBatamLocal === "Yes"
                ? ktpFrontUrl
                : "",
            ktp_back_url:
              nationality === INDONESIA && isBatamLocal === "Yes"
                ? ktpBackUrl
                : "",
            date_of_birth: dateOfBirth || "",
            passport_date_of_issue: dateOfIssue || "",
            passport_expiry_date: expiryDate || "",
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      // was: if (pendingUser.emailVerified) { onAuthenticated(pendingUser); } else { setView("verify"); }
      if (pendingProvider === "singpass" || pendingUser.emailVerified) {
        onAuthenticated(pendingUser);
      } else {
        setView("verify");
      }
    } catch (err) {
      setProfileErrors((e) => ({
        ...e,
        _general:
          err.message || "Failed to save your profile. Please try again.",
      }));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleCheckVerified = async () => {
    if (!pendingUser) return;
    setError("");
    setCheckingVerified(true);
    try {
      await pendingUser.reload();
      if (pendingUser.emailVerified) {
        // Flip the flag in Firestore too so it's reflected everywhere,
        // then — and only then — this is a real, logged-in user.
        await setDoc(
          doc(db, "users", pendingUser.uid),
          { emailVerified: true, updatedAt: serverTimestamp() },
          { merge: true },
        );
        // reload() doesn't reliably re-trigger onAuthStateChanged, so
        // the global AuthContext wouldn't otherwise know this user is
        // now verified until some other auth event (or a page refresh)
        // came along. Force it to sync right now, in place — no
        // navigation, no remount, nothing lost from this checkout flow.
        if (refreshUser) await refreshUser();
        onAuthenticated(pendingUser);
      } else {
        setError(
          "Still not verified — click the link in your inbox first, then try again.",
        );
      }
    } catch (err) {
      setError(
        "Something went wrong checking your verification status. Try again.",
      );
    } finally {
      setCheckingVerified(false);
    }
  };

  const handleResendVerification = async () => {
    if (!pendingUser || resendCooldown) return;
    setError("");
    try {
      await sendEmailVerification(pendingUser);
      setResendCooldown(true);
      setTimeout(() => setResendCooldown(false), 60_000);
    } catch {
      setError("Could not resend right now. Try again in a moment.");
    }
  };

  const closeAndReset = () => {
    setView("login");
    setError("");
    onClose();
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={
        view === "profile" || view === "verify" ? undefined : closeAndReset
      }
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-6">
          <p className="text-[#0B2421] font-bold text-sm tracking-wide">
            {view === "profile"
              ? t.profileEyebrow
              : view === "verify"
                ? ""
                : "Sign in to continue"}
          </p>
          {/* No close button on the profile or verify steps — the account
              already exists at this point, so closing here would just
              strand a signed-in-but-unverified user mid-checkout. */}
          {view !== "profile" && view !== "verify" && (
            <button
              type="button"
              onClick={closeAndReset}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        <div className="px-6 pb-6 pt-2">
          {view === "login" && (
            <>
              <h2 className="text-[#0B2421] font-bold text-xl mb-1">
                Welcome back
              </h2>
              <p className="text-gray-500 text-sm mb-5">
                Please sign in before confirming your payment.
              </p>

              {/* <button
                type="button"
                onClick={handleSingpassSignIn}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 border border-gray-200 rounded-xl py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors mb-3 disabled:opacity-50"
              >
                Continue with Singpass
              </button> */}

              <button
                type="button"
                onClick={() =>
                  handleSocialSignIn(new GoogleAuthProvider(), "google")
                }
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 border border-gray-200 rounded-xl py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors mb-3 disabled:opacity-50"
              >
                <GoogleIcon />
                Continue with Google
              </button>

              <button
                type="button"
                onClick={() =>
                  handleSocialSignIn(new FacebookAuthProvider(), "facebook")
                }
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 rounded-xl bg-[#1877F2] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#166FE5] transition-colors mb-5 disabled:opacity-50"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="white"
                  aria-hidden="true"
                >
                  <path d="M22 12.06C22 6.505 17.523 2 12 2S2 6.505 2 12.06c0 5.02 3.657 9.184 8.438 9.94v-7.03H7.898v-2.91h2.54V9.845c0-2.507 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562v1.877h2.773l-.443 2.91h-2.33V22c4.78-.756 8.437-4.92 8.437-9.94Z" />
                </svg>
                Continue with Facebook
              </button>

              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400 tracking-widest">
                  OR
                </span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              <ErrorBanner msg={error} />

              <InputField
                label="Email"
                type="email"
                placeholder="you@example.com"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
              />
              <InputField
                label="Password"
                type="password"
                placeholder="••••••••"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
              />

              <button
                type="button"
                onClick={handleSignIn}
                disabled={loading}
                className="w-full bg-[#0B2421] hover:bg-[#0B2421]/85 text-white text-sm font-bold rounded-xl py-3 transition-colors mt-2 mb-4 disabled:opacity-50"
              >
                {loading ? "Signing in…" : "Sign In"}
              </button>

              <p className="text-center text-sm text-gray-500">
                No account?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setView("signup");
                    clearError();
                  }}
                  className="text-[#0B2421] font-semibold hover:underline"
                >
                  Create one
                </button>
              </p>
            </>
          )}

          {view === "signup" && (
            <>
              <h2 className="text-[#0B2421] font-bold text-xl mb-1">
                Create account
              </h2>
              <p className="text-gray-500 text-sm mb-5">
                Sign up to continue with your payment.
              </p>

              {/* <button
                type="button"
                onClick={handleSingpassSignIn}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 border border-gray-200 rounded-xl py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors mb-3 disabled:opacity-50"
              >
                Continue with Singpass
              </button> */}

              <button
                type="button"
                onClick={() =>
                  handleSocialSignIn(new GoogleAuthProvider(), "google")
                }
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 border border-gray-200 rounded-xl py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors mb-3 disabled:opacity-50"
              >
                <GoogleIcon />
                Continue with Google
              </button>

              <button
                type="button"
                onClick={() =>
                  handleSocialSignIn(new FacebookAuthProvider(), "facebook")
                }
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 rounded-xl bg-[#1877F2] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#166FE5] transition-colors mb-5 disabled:opacity-50"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="white"
                  aria-hidden="true"
                >
                  <path d="M22 12.06C22 6.505 17.523 2 12 2S2 6.505 2 12.06c0 5.02 3.657 9.184 8.438 9.94v-7.03H7.898v-2.91h2.54V9.845c0-2.507 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562v1.877h2.773l-.443 2.91h-2.33V22c4.78-.756 8.437-4.92 8.437-9.94Z" />
                </svg>
                Continue with Facebook
              </button>

              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400 tracking-widest">
                  OR
                </span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              <ErrorBanner msg={error} />

              <InputField
                label="Full Name"
                placeholder="Alex Morgan"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
              <InputField
                label="Email"
                type="email"
                placeholder="you@example.com"
                value={signupEmail}
                onChange={(e) => setSignupEmail(e.target.value)}
              />
              <InputField
                label="Password"
                type="password"
                placeholder="Min. 6 characters"
                value={signupPassword}
                onChange={(e) => setSignupPassword(e.target.value)}
              />
              <InputField
                label="Confirm Password"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />

              <button
                type="button"
                onClick={handleSignUp}
                disabled={loading}
                className="w-full bg-[#0B2421] hover:bg-[#0B2421]/85 text-white text-sm font-bold rounded-xl py-3 transition-colors mt-2 mb-4 disabled:opacity-50"
              >
                {loading ? "Creating account…" : "Create Account"}
              </button>

              <p className="text-center text-sm text-gray-500">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setView("login");
                    clearError();
                  }}
                  className="text-[#0B2421] font-semibold hover:underline"
                >
                  Sign in
                </button>
              </p>
            </>
          )}

          {view === "profile" && (
            <>
              <h2 className="text-[#0B2421] font-bold text-xl mb-1">
                {t.profileTitle}
              </h2>
              <p className="text-gray-500 text-sm mb-5">
                {t.profileSubtext}
                {pendingProvider === "email" && ` ${t.profileSubtextEmailNote}`}
              </p>

              <ErrorBanner msg={profileErrors._general} />

              <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">
                {t.sectionContact}
              </p>
              <div className="flex gap-2 mb-1">
                <select
                  value={mobileCountryCode}
                  onChange={(e) => setMobileCountryCode(e.target.value)}
                  className="h-11 w-36 shrink-0 truncate rounded-xl border border-gray-200 bg-gray-50 pl-2 pr-1 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-[#0B2421]/30"
                >
                  {DIAL_CODES.map((d) => (
                    <option key={`${d.country}-${d.dial}`} value={d.dial}>
                      {d.dial} {d.country}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  inputMode="numeric"
                  placeholder="Mobile number"
                  value={mobileNumber}
                  onChange={(e) =>
                    setMobileNumber(e.target.value.replace(/[^0-9]/g, ""))
                  }
                  className="h-11 flex-1 rounded-xl border border-gray-200 bg-gray-50 px-3.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-[#0B2421]/30"
                />
              </div>
              {profileErrors.mobileNumber && (
                <p className="text-xs text-red-500 mt-1 mb-2">
                  {profileErrors.mobileNumber}
                </p>
              )}

              <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2 mt-4">
                {t.sectionPassport}
              </p>

              <InputField
                label={t.fieldFullName}
                placeholder={t.fieldFullNamePlaceholder}
                value={fullNameOnPassport}
                onChange={(e) =>
                  setFullNameOnPassport(e.target.value.toUpperCase())
                }
                error={profileErrors.fullNameOnPassport}
              />
              <InputField
                label={t.fieldPassportNumber}
                placeholder={t.fieldPassportNumberPlaceholder}
                value={passportNumber}
                onChange={(e) =>
                  setPassportNumber(e.target.value.toUpperCase())
                }
                error={profileErrors.passportNumber}
              />

              <div className="grid grid-cols-2 gap-3">
                <SelectField
                  label={t.fieldNationality}
                  value={nationality}
                  onChange={(e) => handleNationalityChange(e.target.value)}
                  options={COUNTRIES.map((c) => c.name)}
                  placeholder="Select"
                  error={profileErrors.nationality}
                />
                <SelectField
                  label={t.fieldBirthCountry}
                  value={birthCountry}
                  onChange={(e) => setBirthCountry(e.target.value)}
                  options={COUNTRIES.map((c) => c.name)}
                  placeholder="Select"
                  error={profileErrors.birthCountry}
                />
              </div>

              {nationality === INDONESIA && (
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    {t.fieldBatamLocal}
                  </label>
                  <div className="flex items-center gap-6">
                    {["Yes", "No"].map((opt) => (
                      <label
                        key={opt}
                        className="flex items-center gap-2 cursor-pointer text-sm text-gray-700"
                      >
                        <input
                          type="radio"
                          name="isBatamLocal"
                          value={opt}
                          checked={isBatamLocal === opt}
                          onChange={() => setIsBatamLocal(opt)}
                          className="h-4 w-4 accent-[#0B2421] cursor-pointer"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                  {profileErrors.isBatamLocal && (
                    <p className="text-xs text-red-500 mt-1">
                      {profileErrors.isBatamLocal}
                    </p>
                  )}
                </div>
              )}

              {nationality === INDONESIA && isBatamLocal === "Yes" && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-gray-500 mb-1.5">
                    {t.ktpUploadInstruction}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <KtpUploadBox
                      label={t.fieldKtpFront}
                      value={ktpFrontUrl}
                      isUploading={isUploadingKtpFront}
                      onSelect={(file) => handleKtpUpload("front", file)}
                      error={profileErrors.ktpFrontUrl}
                    />
                    <KtpUploadBox
                      label={t.fieldKtpBack}
                      value={ktpBackUrl}
                      isUploading={isUploadingKtpBack}
                      onSelect={(file) => handleKtpUpload("back", file)}
                      error={profileErrors.ktpBackUrl}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">
                    {t.ktpFileConstraints}
                  </p>
                </div>
              )}

              <SelectField
                label={t.fieldGender}
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                options={GENDER_OPTIONS}
                placeholder="Select gender"
                error={profileErrors.gender}
              />

              <div className="grid grid-cols-2 gap-3">
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    {t.fieldDateOfBirth}
                  </label>
                  <input
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    className="w-full h-11 rounded-xl border border-gray-200 bg-gray-50 px-3.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-[#0B2421]/30"
                  />
                  {profileErrors.dateOfBirth && (
                    <p className="text-xs text-red-500 mt-1">
                      {profileErrors.dateOfBirth}
                    </p>
                  )}
                </div>
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    {t.fieldDateOfIssue} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={dateOfIssue}
                    onChange={(e) => setDateOfIssue(e.target.value)}
                    className={`w-full h-11 rounded-xl border bg-gray-50 px-3.5 text-sm text-gray-700 outline-none focus:ring-2 ${
                      profileErrors.dateOfIssue
                        ? "border-red-400 ring-2 ring-red-100 focus:ring-red-200"
                        : "border-gray-200 focus:ring-[#0B2421]/30"
                    }`}
                  />
                  {profileErrors.dateOfIssue && (
                    <p className="text-xs text-red-500 mt-1">
                      {profileErrors.dateOfIssue}
                    </p>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  {t.fieldExpiryDate}
                </label>
                <input
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  className="w-full h-11 rounded-xl border border-gray-200 bg-gray-50 px-3.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-[#0B2421]/30"
                />
                {profileErrors.expiryDate && (
                  <p className="text-xs text-red-500 mt-1">
                    {profileErrors.expiryDate}
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={handleFinishProfile}
                disabled={savingProfile}
                className="w-full bg-[#0B2421] hover:bg-[#0B2421]/85 text-white text-sm font-bold rounded-xl py-3 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {savingProfile ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t.savingLabel}
                  </>
                ) : (
                  t.saveButton
                )}
              </button>
            </>
          )}

          {view === "verify" && (
            <>
              <h2 className="text-[#0B2421] font-bold text-xl mb-2 text-center">
                Check your email
              </h2>
              <p className="text-gray-500 text-sm mb-1 text-center">
                We sent a verification link to
              </p>
              <p className="text-[#0B2421] font-semibold text-sm mb-5 text-center">
                {pendingUser?.email}
              </p>

              <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mb-5 text-sm text-gray-500 text-center leading-relaxed">
                Click the link in the email, then come back and tap the button
                below. This is the last step — your booking is saved and waiting
                for you.
              </div>

              <ErrorBanner msg={error} />

              <button
                type="button"
                onClick={handleCheckVerified}
                disabled={checkingVerified}
                className="w-full bg-[#0B2421] hover:bg-[#0B2421]/85 text-white text-sm font-bold rounded-xl py-3 transition-colors mb-4 disabled:opacity-50"
              >
                {checkingVerified ? "Checking…" : "I've verified my email"}
              </button>

              <div className="flex flex-col items-center gap-2">
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendCooldown}
                  className="text-sm text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-40"
                >
                  {resendCooldown
                    ? "Link sent — check your inbox"
                    : "Resend verification email"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Bordered upload box for a single KTP card image (front or back), with a
 * thumbnail preview + "Replace" once an image has been uploaded.
 * @param {{ label: string; value: string; isUploading: boolean; onSelect: (file: File) => void; error?: string }} props
 */
function KtpUploadBox({ label, value, isUploading, onSelect, error }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">
        {label}
      </label>
      {value && !isUploading ? (
        <div className="relative group rounded-xl overflow-hidden border border-gray-200 h-24">
          {value.toLowerCase().includes(".pdf") ? (
            <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-gray-50">
              <FileText className="h-5 w-5 text-gray-400" />
              <span className="text-xs text-gray-500">PDF uploaded</span>
            </div>
          ) : (
            <img
              src={value}
              alt={label}
              className="w-full h-full object-cover"
            />
          )}
          <label className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer">
            <span className="px-3 py-1.5 rounded-lg bg-white text-xs font-semibold">
              Replace
            </span>
            <input
              type="file"
              accept="image/*,.avif,application/pdf"
              className="hidden"
              onChange={(e) => onSelect(e.target.files?.[0])}
            />
          </label>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center h-24 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors">
          {isUploading ? (
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          ) : (
            <>
              <UploadCloud className="h-5 w-5 text-gray-400 mb-1" />
              <span className="text-xs text-gray-500">Upload</span>
            </>
          )}
          <input
            type="file"
            accept="image/*,.avif,application/pdf"
            className="hidden"
            onChange={(e) => onSelect(e.target.files?.[0])}
          />
        </label>
      )}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}
